import React, { useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useTheme } from '@mui/material/styles'
import { ThemeProvider as StyledThemeProvider } from 'styled-components'
import Button from '@/v2/components/common/Button'
import {
  useGetPublicIntakeForm,
  useSubmitIntakeResponse,
} from '@/v2/hooks/health-history/useHealthHistoryForm'
import { buildHealthHistorySchema } from './rules'
import { buildPayload, mapServerErrors } from './utils'
import { INVALID_ATTR } from './fields'
import PersonalInfo from './sections/PersonalInfo'
import EmergencyContact from './sections/EmergencyContact'
import DentalHistory from './sections/DentalHistory'
import DentalIssues from './sections/DentalIssues'
import MedicalHistory from './sections/MedicalHistory'
import MedicalIssues from './sections/MedicalIssues'
import Comments from './sections/Comments'
import SignatureTerms from './sections/SignatureTerms'
import {
  ScrollableWrapper,
  PageContainer,
  FullScreenCard,
  ButtonContainer,
  WarningBox,
} from './styles'

const EMPTY = { dental_conditions: [], medical_issues: [] }

// The 410 and success payloads both carry the clinic block so a patient who
// can no longer use the link still has somewhere to turn.
const ClinicContact = ({ clinic }) => {
  if (!clinic?.name) return null

  return (
    <p>
      {clinic.name}
      {clinic.address && <>, {clinic.address}</>}
      {clinic.phone_number && <> — {clinic.phone_number}</>}
    </p>
  )
}

// Patient-facing fill form only. Staff view the submitted answers on the legacy
// /intake_forms/:id page, which also offers the PDF download.
const HealthHistoryForm = () => {
  const params = useParams()
  const uuid = params?.uuid
  // The MUI theme is already provided by the app root (ThemeContainer), which
  // wraps every route—including public/unauthenticated ones. Read the ambient
  // theme and bridge it to styled-components for ./styles.
  const theme = useTheme()

  const publicQuery = useGetPublicIntakeForm(uuid)
  const { submitResponse, isPending } = useSubmitIntakeResponse(uuid)

  const [values, setValues] = useState(EMPTY)
  const [errors, setErrors] = useState({})
  const [submitted, setSubmitted] = useState(null)
  const [submitError, setSubmitError] = useState('')

  const { setting, options } = publicQuery

  const schema = useMemo(
    () => buildHealthHistorySchema(setting, options),
    [setting, options]
  )

  // Bring the first problem into view after a rejected submit — the form is
  // long enough that the warning banner alone leaves patients hunting.
  // `errors` is replaced (never mutated) on every submit, so a repeat submit
  // with the same failures still re-triggers this.
  useEffect(() => {
    if (!Object.keys(errors).length) return

    const field = document.querySelector(`[${INVALID_ATTR}]`)
    if (!field) return

    field.scrollIntoView({ behavior: 'smooth', block: 'center' })
    // preventScroll so focus doesn't race the smooth scroll to a different spot.
    field.querySelector('input, textarea, select')?.focus({ preventScroll: true })
  }, [errors])

  const setValue = (field, value) =>
    setValues(prev => ({ ...prev, [field]: value }))

  const handleSubmit = async () => {
    setErrors({})
    setSubmitError('')
    try {
      await schema.validate(values, { abortEarly: false })
    } catch (err) {
      const next = {}
      ;(err.inner || []).forEach(e => {
        if (e.path && !next[e.path]) next[e.path] = e.message
      })
      setErrors(next)
      return
    }

    const payload = buildPayload(values)
    // The API rejects anything but a literal `true` for accepts_conditions,
    // even when the clinic shows no terms block and the checkbox never renders.
    // Auto-accept in that case so the form stays submittable.
    if (!setting.terms_enabled) {
      payload.intake_response.accepts_conditions = true
    }
    let result
    try {
      result = await submitResponse(payload)
    } catch {
      // requestMiddleware re-throws whenever the body is not JSON — a Rails
      // error page, a proxy 502. Without this the rejection goes unhandled and
      // Submit does nothing at all: no screen, no banner, no snackbar.
      // Deliberately does not say "try again": the server may have persisted
      // the response before failing, in which case a retry just 410s.
      setSubmitError(
        "We couldn't confirm your form was submitted. Please contact the clinic before filling it in again."
      )
      return
    }

    if (result && !result.error) {
      setSubmitted(result)
    } else if (result?.error && typeof result.error === 'object') {
      setErrors(prev => ({ ...prev, ...mapServerErrors(result.error) }))
    }
    // A string `error` (410 expired, invalid option, 429 rate limit) is already
    // surfaced as a snackbar by requestMiddleware.
  }

  let content = null

  if (publicQuery.isLoading) {
    content = <PageContainer>Loading…</PageContainer>
  } else if (publicQuery.isError || !publicQuery.isLoaded) {
    content = (
      <FullScreenCard>
        <h2>Form unavailable</h2>
        <p>We couldn't load this health history form. Please contact the clinic.</p>
      </FullScreenCard>
    )
  } else if (publicQuery.isExpired) {
    content = (
      <FullScreenCard>
        <h2>This link is no longer valid</h2>
        <p>
          This form may have already been completed. Please contact the clinic
          if you need help.
        </p>
        <ClinicContact clinic={publicQuery.clinic} />
      </FullScreenCard>
    )
  } else if (submitted) {
    content = (
      <FullScreenCard>
        <h2>Thank you!</h2>
        <p>{submitted.message || 'Your health history form has been submitted.'}</p>
        {/* The API returns patientEmail but sends no email on submit, so this
            confirms the address on file rather than claiming a copy was sent. */}
        {submitted.patientEmail && (
          <p>
            Submitted for <strong>{submitted.patientEmail}</strong>.
          </p>
        )}
        <ClinicContact clinic={publicQuery.clinic} />
      </FullScreenCard>
    )
  }

  if (!content) {
    const hasErrors = Object.keys(errors).length > 0

    content = (
      <ScrollableWrapper>
        <PageContainer>
          <h1>{setting.title || 'Health History Form'}</h1>
          {setting.introduction && <p>{setting.introduction}</p>}
          {hasErrors && (
            <WarningBox>Please fill in the required fields highlighted below.</WarningBox>
          )}
          <PersonalInfo
            values={values}
            setValue={setValue}
            errors={errors}
            genders={options.genders}
          />
          <EmergencyContact values={values} setValue={setValue} errors={errors} />
          <DentalHistory values={values} setValue={setValue} errors={errors} />
          <DentalIssues
            values={values}
            setValue={setValue}
            errors={errors}
            dentalConditions={options.dental_conditions}
          />
          <MedicalHistory values={values} setValue={setValue} errors={errors} />
          <MedicalIssues
            values={values}
            setValue={setValue}
            medicalIssues={options.medical_issues}
          />
          <Comments values={values} setValue={setValue} />
          <SignatureTerms
            setting={setting}
            values={values}
            setValue={setValue}
            errors={errors}
          />
          {/* Sits by the button rather than at the top of the page: the patient
              is at the bottom of a long form when they hit Submit. */}
          {submitError && <WarningBox>{submitError}</WarningBox>}
          <ButtonContainer>
            {/* `secondary` is the same accent the step circles use; without it
                MUI's default text variant renders a plain black link. */}
            <Button
              variant="contained"
              color="secondary"
              onClick={handleSubmit}
              disabled={isPending}
            >
              {isPending ? 'Submitting…' : 'Submit'}
            </Button>
          </ButtonContainer>
        </PageContainer>
      </ScrollableWrapper>
    )
  }

  return <StyledThemeProvider theme={theme}>{content}</StyledThemeProvider>
}

export default HealthHistoryForm
